import {
  forwardRef,
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react'
import { IconCheck, IconCopy } from './icons'

/* -------------------------------------------------------------------------
   Button
   ------------------------------------------------------------------------- */

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: 'sm' | 'md'
  /** Shows a spinner and blocks further clicks. */
  loading?: boolean
  block?: boolean
}

const VARIANT: Record<Variant, string> = {
  primary: 'primary',
  secondary: '',
  danger: 'danger',
  ghost: 'ghost',
}

/* Forwards its ref so tooltips and menus can anchor to it — every overlay
   here positions itself against a real element. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    block = false,
    className = '',
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const classes = [
    'btn',
    VARIANT[variant],
    size === 'sm' ? 'sm' : '',
    block ? 'block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button ref={ref} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  )
})

/* -------------------------------------------------------------------------
   Status pill
   ------------------------------------------------------------------------- */

export type Tone = 'ok' | 'busy' | 'warn' | 'bad' | 'idle'

/* Forwards its ref so a tooltip can anchor to it. A pill is not focusable on
   purpose: there is one per row, and a tab stop per row to read a hint that
   the label already summarises is a bad trade. */
export const Pill = forwardRef<
  HTMLSpanElement,
  { tone: Tone; children: ReactNode }
>(function Pill({ tone, children, ...rest }, ref) {
  return (
    <span className={`pill ${tone}`} ref={ref} {...rest}>
      <span className="dot" aria-hidden="true" />
      {children}
    </span>
  )
})

/* -------------------------------------------------------------------------
   Skeletons — shaped like the thing they stand in for, so the layout does
   not jump when the real content lands.
   ------------------------------------------------------------------------- */

export function Skeleton({
  width = '100%',
  height = 14,
  radius,
}: {
  width?: string | number
  height?: string | number
  radius?: number
}) {
  return (
    <span
      className="sk"
      style={{
        display: 'block',
        width,
        height,
        borderRadius: radius,
      }}
    />
  )
}

export function CardSkeleton() {
  return (
    <div className="card" aria-hidden="true">
      <div className="card-head">
        <Skeleton width="45%" height={16} />
        <Skeleton width={64} height={18} radius={999} />
      </div>
      <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
        <Skeleton width="70%" height={11} />
        <Skeleton width="52%" height={11} />
      </div>
    </div>
  )
}

export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="list" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div className="list-item" key={index}>
          <Skeleton width={72} height={18} radius={999} />
          <div className="grow" style={{ display: 'grid', gap: 7 }}>
            <Skeleton width="42%" height={12} />
            <Skeleton width="26%" height={10} />
          </div>
        </div>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Empty state
   ------------------------------------------------------------------------- */

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      {icon}
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Copy to clipboard
   ------------------------------------------------------------------------- */

export function CopyButton({
  value,
  label = 'Copy',
  size = 'sm',
}: {
  value: string
  label?: string
  size?: 'sm' | 'md'
}) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number>()

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Clipboard access needs a secure context; fall back to a selection.
      const field = document.createElement('textarea')
      field.value = value
      document.body.appendChild(field)
      field.select()
      document.execCommand('copy')
      field.remove()
    }
    setCopied(true)
    timer.current = window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <Button size={size} onClick={copy} aria-label={copied ? 'Copied' : label}>
      {copied ? <IconCheck size={14} className="" /> : <IconCopy size={14} className="" />}
      {copied ? 'Copied' : label}
    </Button>
  )
}

/* -------------------------------------------------------------------------
   Field
   ------------------------------------------------------------------------- */

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor?: string
  hint?: ReactNode
  children: ReactNode
}) {
  const generated = useId()
  const id = htmlFor ?? generated
  const child = isValidElement(children) && typeof children.type === 'string' && ['input', 'select', 'textarea'].includes(children.type) ? children as ReactElement<{ id?: string; 'aria-describedby'?: string }> : null
  const describedBy = [child?.props['aria-describedby'], hint ? `${id}-hint` : null].filter(Boolean).join(' ') || undefined
  return (
    <div className="field">
      <label htmlFor={child ? child.props.id ?? id : htmlFor}>{label}</label>
      {child ? cloneElement(child, { id: child.props.id ?? id, 'aria-describedby': describedBy }) : children}
      {hint && <div className="hint" id={`${id}-hint`}>{hint}</div>}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Confirm dialog — replaces window.confirm, which cannot explain consequences
   and cannot require the user to type anything.
   ------------------------------------------------------------------------- */

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  /** When set, the button unlocks only once this exact text is typed. */
  requireText,
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  requireText?: string
  destructive?: boolean
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [typed, setTyped] = useState('')

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      setTyped('')
      dialog.showModal()
    }
    if (!open && dialog.open) dialog.close()
  }, [open])

  const unlocked =
    !requireText || typed.trim().toLowerCase() === requireText.toLowerCase()

  return (
    <dialog ref={ref} onCancel={event => { if (busy) event.preventDefault(); else onCancel() }} onClose={() => { if (!busy) onCancel() }} aria-label={title}>
      <div className="sheet">
        <div className="stack" style={{ gap: 8 }}>
          <h2>{title}</h2>
          <div className="muted">{body}</div>
        </div>

        {requireText && (
          <Field
            label={`Type ${requireText} to confirm`}
            htmlFor="confirm-text"
          >
            <input
              id="confirm-text"
              className="mono"
              value={typed}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field>
        )}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={!unlocked}
            loading={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  )
}

